import * as yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum MagicMindPurchaseOrderLineItemKeys {
  itemNumber = 'itemNumber',
  itemName = 'itemName',
  name = 'name',
  description = 'description',
  itemQty = 'itemQty',
  unitPrice = 'unitPrice',
  lineTotal = 'lineTotal',
}

const magicMindPOAddressInfoSchema = yup.object().shape({
  attn: yup.string().nullable().optional(),
  companyName: yup.string().nullable().optional(),
  addressLine1: yup.string().nullable().optional(),
  addressLine2: yup.string().nullable().optional(),
  city: yup.string().nullable().optional(),
  state: yup.string().nullable().optional(),
  zipCode: yup.string().nullable().optional(),
  country: yup.string().nullable().optional(),
  phoneNumber: yup.string().nullable().optional(),
  emailAddress: yup.string().nullable().optional(),
  displayMultipleLines: yup.string().nullable().optional(),
})

const magicMindPOLineItemSchema = yup.object().shape({
  itemNumber: yup.number().nullable().optional(),
  itemName: yup.string().nullable().optional(),
  name: yup.string().nullable().optional(),
  description: yup.string().nullable().optional(),
  itemQty: yup.string().nullable().optional(),
  unitPrice: yup.string().nullable().optional(),
  lineTotal: yup.string().nullable().optional(),
})

export const magicMindPurchaseOrderFileDataSchema = yup.object().shape({
  issuer: magicMindPOAddressInfoSchema,
  orderNumber: yup.string().nullable().optional(),
  dateIssued: yup.string().nullable().optional(),
  dateReady: yup.string().nullable().optional(),
  shipDate: yup.string().nullable().optional(),
  paymentTerms: yup.string().nullable().optional(),
  freightTerms: yup.string().nullable().optional(),
  destination: magicMindPOAddressInfoSchema,
  vendor: magicMindPOAddressInfoSchema,
  items: yup.array().of(magicMindPOLineItemSchema).required(),
  items2: yup.array().of(magicMindPOLineItemSchema).optional(),
  dontRoundUnitPrice: yup.string().nullable().optional(),
  agreement: yup.string().nullable().optional(),
  instructions: yup.string().nullable().optional(),
  useLocaleQuantityFormatting: yup.string().nullable().optional(),
})

export type MagicMindPurchaseOrderFileData = yup.InferType<
  typeof magicMindPurchaseOrderFileDataSchema
>
type RequiredFileData = MakeRequiredAndNullable<MagicMindPurchaseOrderFileData>

export const magicMindPurchaseOrderFileMappings: RequiredFileData = {
  issuer: {
    attn: '8376f47f-24cd-4643-8390-28cf1c3cd94e',
    companyName: '9a512e45-41d7-4d45-85dc-7b8a7b3ea3d7',
    addressLine1: '80572ecb-2762-49f9-84d6-1de0880eeba1',
    addressLine2: '813b4347-cc24-4112-b894-0b379f50b6da',
    city: 'd9e48e05-5753-4859-9d9c-28502b2b2ab5',
    state: '0445558d-e82f-44ca-b47b-1129d8a80d11',
    zipCode: '1ee3240a-683f-45a8-a60e-03a4eb821b24',
    country: '9e81e3b1-a8d1-495b-a987-09786bb6fe46',
    phoneNumber: '839cb4e1-70e1-4c00-a09b-61a3f7af55ed',
    emailAddress: 'b42f056d-0c73-4150-9d31-946acfc36e57',
    displayMultipleLines: '7266cb51-828d-44a6-a70b-78ff19022798',
  },
  orderNumber: 'f46cb050-807d-43a4-a1c8-b17e0663cf4b',
  paymentTerms: '239df059-f06c-4d02-840f-ceaac1c8d9e7',
  freightTerms: 'd47fc374-abd7-485c-8651-3412c269f77b',
  dateIssued: 'c060af01-4124-4b8e-8016-6d72f2054ee9',
  dateReady: '12fe5863-953d-46d5-98b7-2baeda40fc60',
  shipDate: 'cee070c4-a5a5-4fc9-ad21-74794f911e7e',
  destination: {
    attn: '3b7f9558-ce6c-446e-8488-29cb72d9e339',
    companyName: 'de8c17d5-fc4d-494a-8876-0b58ee5be9fb',
    addressLine1: 'aad98357-6fab-4ac2-8f50-1e0c391550f2',
    addressLine2: 'd5722523-c07b-4d19-a6d7-99858d56bb1e',
    city: '79ee410a-6af4-49c8-86c1-c7891dbe701c',
    state: 'deb6ac7f-040b-4382-8f33-90a51ec889de',
    zipCode: 'c2d86aa1-f3c5-4913-9111-f90a78b1d71c',
    country: 'c2dd12ab-1662-440f-b8d2-936570a9fd07',
    phoneNumber: '1b5ee39b-b488-47d6-a997-ace277846079',
    emailAddress: '15bb94f3-773b-4689-a638-70e08daeb686',
    displayMultipleLines: '1d4ce168-37d0-4d1f-84b6-acb449ea6aaa',
  },
  vendor: {
    attn: 'e5f2d2be-3c32-4f6f-a43a-2fc8fab98c6c',
    companyName: '740e547a-a993-465b-a3ee-3121e37d1637',
    addressLine1: 'e4f2cec9-15ae-4aef-aadc-389ffba6b025',
    addressLine2: '2dbd68cc-b1aa-4380-9422-48bcd73672cd',
    city: '6f13f7ed-66e8-4971-b184-310b3af25e16',
    state: '59ad4992-b1f0-4532-b7a7-912ed1a2f02b',
    zipCode: '440c3edb-c877-4dbf-b01a-d12731b5b7e1',
    country: '8dcddcd5-1504-4305-83f3-efafbbf04ad1',
    phoneNumber: 'bde4aac6-4c07-4e08-b7e7-1a5e165de888',
    emailAddress: '70c4bd09-1a77-4464-9063-817d82d2c760',
    displayMultipleLines: 'a6ba6676-a175-4cfb-a5f0-e2e1904a068d',
  },
  items: [
    {
      itemNumber: null,
      itemName: '9e807bb5-a472-4eef-921d-80e0f50ca886',
      name: '8624cdc0-3ab6-4a41-bad1-8d61b29e809d',
      description: 'c68bb622-2d77-43c3-8dc4-0890b3431603',
      itemQty: 'fb5a126c-fed9-47de-beca-9b3341d3e2eb',
      unitPrice: 'a71ee321-ab39-400b-b201-f473a0f0b17b',
      lineTotal: null,
    },
  ],
  items2: [
    {
      itemNumber: null,
      itemName: 'dc27618e-83d0-42ba-9b10-3e5e36302b81',
      name: '722fa87b-40e8-40f4-ac30-ff4413174e78',
      description: '63cf9075-b84e-46ec-be48-8526440cb256',
      itemQty: 'b3773944-6482-4ae4-8845-52395c10c901',
      unitPrice: '7053345f-a777-4656-8ff6-005d5d2f068e',
      lineTotal: null,
    },
  ],
  dontRoundUnitPrice: '83881613-fac6-4f90-ba62-5043db1f5d6b',
  agreement: '0d9ac395-28de-4fb0-9e7e-4d3f6e5d2773',
  instructions: 'f700423e-1d07-4153-9d0d-962e5575450d',
  useLocaleQuantityFormatting: '40a1891c-89b5-4516-b56a-46caf1b99a19',
}
